import { useState, type ReactElement } from 'react';
import { Link, useParams } from 'react-router';
import { messageForError } from '@proctoring/api-client';
import type { Invitation, User } from '@proctoring/shared';
import {
  downloadTelemetryExport,
  useCancelInterview,
  useInterview,
  useInvitations,
  useRescheduleInterview,
  useResendInvitation,
  useRevokeInvitation,
  useTelemetry,
} from '../api/queries.js';
import { useApi } from '../auth/AuthProvider.js';
import { FullPageMessage } from '../components/FullPageMessage.js';
import { StateBadge } from '../components/StateBadge.js';
import { TelemetryLogView } from '../components/TelemetryLogView.js';
import { formatDateTime } from '../components/format.js';

type InvitationStatus = 'revoked' | 'accepted' | 'expired' | 'pending';

/**
 * Derives the status an admin actually cares about from the invitation's timestamps.
 *
 * Order matters: a revoked invitation is revoked regardless of whether it had already been
 * used, and an accepted one is never "expired" — the account exists either way.
 */
export function invitationStatus(invitation: Invitation, now: Date): InvitationStatus {
  if (invitation.revokedAt) return 'revoked';
  if (invitation.usedAt) return 'accepted';
  if (Date.parse(invitation.expiresAt) <= now.getTime()) return 'expired';
  return 'pending';
}

const STATUS_CLASSES: Record<InvitationStatus, string> = {
  pending: 'border-severity-warning/40 bg-severity-warning/10 text-severity-warning',
  accepted: 'border-severity-info/40 bg-severity-info/10 text-severity-info',
  revoked: 'border-severity-critical/40 bg-severity-critical/10 text-severity-critical',
  expired: 'border-session-border bg-session-panel-raised text-session-dim',
};

function InvitationRow({
  invitation,
  people,
  interviewId,
}: {
  invitation: Invitation;
  people: Map<string, User>;
  interviewId: string;
}): ReactElement {
  const revoke = useRevokeInvitation(interviewId);
  const resend = useResendInvitation(interviewId);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [resent, setResent] = useState(false);

  const status = invitationStatus(invitation, new Date());
  const person = people.get(invitation.userId);
  const revocable = status === 'pending' || status === 'accepted';
  /*
   * The two states an admin gets asked about — "I never got the email" and "the link
   * expired" — and the only two a resend can fix. An accepted invitation needs a password
   * reset instead, and a revoked one was deliberately killed; offering a resend there would
   * invite an admin to undo their own revocation by accident.
   */
  const resendable = status === 'pending' || status === 'expired';

  return (
    <li
      data-testid="invitation-row"
      data-status={status}
      className="flex items-center justify-between gap-4 border-b border-session-border px-5 py-4 last:border-b-0"
    >
      <div className="min-w-0">
        <p className="truncate text-sm text-session-text">
          {person ? person.fullName : 'Unknown user'}
          <span className="ml-2 text-xs text-session-dim">{person?.email}</span>
        </p>
        <p className="mt-0.5 text-xs text-session-dim">
          Expires {formatDateTime(invitation.expiresAt)}
        </p>
        {error ? (
          <p role="alert" className="mt-1 text-xs text-severity-critical">
            {error}
          </p>
        ) : null}
      </div>

      <div className="flex shrink-0 items-center gap-3">
        <span
          className={`rounded-full border px-2.5 py-0.5 text-xs font-medium capitalize ${STATUS_CLASSES[status]}`}
        >
          {status}
        </span>

        {resendable ? (
          <button
            type="button"
            onClick={() => {
              setError(null);
              setResent(false);
              resend
                .mutateAsync(invitation.id)
                .then(() => setResent(true))
                .catch((cause: unknown) => setError(messageForError(cause)));
            }}
            disabled={resend.isPending}
            className="btn-secondary px-3 py-1.5 text-xs"
          >
            {resend.isPending ? 'Sending…' : resent ? 'Sent' : 'Resend'}
          </button>
        ) : null}

        {revocable ? (
          confirming ? (
            <>
              <button
                type="button"
                onClick={() => {
                  setError(null);
                  revoke
                    .mutateAsync(invitation.id)
                    .then(() => setConfirming(false))
                    .catch((cause: unknown) => setError(messageForError(cause)));
                }}
                disabled={revoke.isPending}
                className="btn-danger px-3 py-1.5 text-xs"
              >
                {revoke.isPending ? 'Revoking…' : 'Confirm revoke'}
              </button>
              <button
                type="button"
                onClick={() => setConfirming(false)}
                className="btn-secondary px-3 py-1.5 text-xs"
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => setConfirming(true)}
              className="btn-danger px-3 py-1.5 text-xs"
            >
              Revoke
            </button>
          )
        ) : null}
      </div>
    </li>
  );
}

/** `scheduledAt` as the value a `datetime-local` input wants: local time, no zone, no seconds. */
function toLocalInputValue(iso: string): string {
  const date = new Date(iso);
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
}

/**
 * Rescheduling and cancellation, the two administrative actions a booked interview needs.
 *
 * Both are hidden once the interview is over: a completed or cancelled interview is a
 * historical record, and the API refuses to move or re-cancel one — showing controls that
 * can only fail is worse than showing none.
 */
function ScheduleControls({
  interview,
}: {
  interview: { id: string; state: string; scheduledAt: string };
}): ReactElement | null {
  const reschedule = useRescheduleInterview(interview.id);
  const cancel = useCancelInterview(interview.id);

  const [scheduledAt, setScheduledAt] = useState(() => toLocalInputValue(interview.scheduledAt));
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const finished = interview.state === 'completed' || interview.state === 'cancelled';
  if (finished) return null;

  const onReschedule = (): void => {
    setError(null);
    setNotice(null);
    const parsed = new Date(scheduledAt);
    if (Number.isNaN(parsed.getTime())) {
      setError('Choose a valid date and time.');
      return;
    }
    reschedule
      .mutateAsync({ scheduledAt: parsed.toISOString() })
      .then(() => setNotice('Rescheduled. Both sides have been emailed.'))
      .catch((cause: unknown) => setError(messageForError(cause)));
  };

  const onCancel = (): void => {
    setError(null);
    setNotice(null);
    cancel
      .mutateAsync(reason.trim().length > 0 ? { reason: reason.trim() } : {})
      .then(() => setConfirmingCancel(false))
      .catch((cause: unknown) => setError(messageForError(cause)));
  };

  return (
    <section className="panel mt-6 p-5" aria-labelledby="schedule">
      <h2 id="schedule" className="text-sm font-semibold text-session-text">
        Schedule
      </h2>

      <div className="mt-4 flex flex-wrap items-end gap-3">
        <div>
          <label htmlFor="scheduled-at" className="field-label">
            Scheduled at
          </label>
          <input
            id="scheduled-at"
            type="datetime-local"
            value={scheduledAt}
            onChange={(event) => setScheduledAt(event.target.value)}
            className="field-input mt-1"
          />
        </div>
        <button
          type="button"
          onClick={onReschedule}
          disabled={reschedule.isPending}
          className="btn-primary px-3 py-2 text-xs"
        >
          {reschedule.isPending ? 'Rescheduling…' : 'Reschedule'}
        </button>
      </div>
      <p className="mt-2 text-xs text-session-dim">
        Shown in your local time. Both the candidate and the interviewers are emailed the new time.
      </p>

      <div className="mt-5 border-t border-session-border pt-5">
        {confirmingCancel ? (
          <div className="space-y-3">
            <p className="text-sm text-session-text">
              Cancel this interview? The room stops being joinable, outstanding invitation links are
              revoked, and both sides are emailed. This cannot be undone.
            </p>
            <div>
              <label htmlFor="cancel-reason" className="field-label">
                Reason (recorded in the audit log, not shown to the candidate)
              </label>
              <input
                id="cancel-reason"
                type="text"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                maxLength={500}
                className="field-input mt-1 w-full"
              />
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onCancel}
                disabled={cancel.isPending}
                className="btn-danger px-3 py-1.5 text-xs"
              >
                {cancel.isPending ? 'Cancelling…' : 'Confirm cancel'}
              </button>
              <button
                type="button"
                onClick={() => setConfirmingCancel(false)}
                className="btn-secondary px-3 py-1.5 text-xs"
              >
                Keep interview
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setConfirmingCancel(true)}
            className="btn-danger px-3 py-1.5 text-xs"
          >
            Cancel interview
          </button>
        )}
      </div>

      {error ? (
        <p role="alert" className="form-error mt-3">
          {error}
        </p>
      ) : null}
      {notice ? <p className="form-notice mt-3">{notice}</p> : null}
    </section>
  );
}

export function InterviewDetailPage(): ReactElement {
  const { interviewId = '' } = useParams();
  const api = useApi();
  const { data: interview, isPending, error } = useInterview(interviewId);
  const invitations = useInvitations(interviewId);
  const telemetry = useTelemetry(interviewId);

  const [exportError, setExportError] = useState<string | null>(null);
  const [exporting, setExporting] = useState<'json' | 'csv' | null>(null);

  if (isPending) return <FullPageMessage title="Loading interview…" />;
  if (error) {
    return <FullPageMessage title="Could not load interview" detail={messageForError(error)} />;
  }

  const people = new Map<string, User>(
    [interview.candidate, ...interview.interviewers].map((person) => [person.id, person]),
  );

  async function onExport(format: 'json' | 'csv'): Promise<void> {
    setExportError(null);
    setExporting(format);
    try {
      await downloadTelemetryExport(api, interviewId, format);
    } catch (cause) {
      setExportError(messageForError(cause));
    } finally {
      setExporting(null);
    }
  }

  return (
    <div className="mx-auto w-full max-w-4xl px-6 py-10">
      <Link to="/interviews" className="text-sm text-session-muted hover:text-session-text">
        ← All interviews
      </Link>

      <div className="mt-4 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-session-text">
            {interview.jobProfile.name}
          </h1>
          <p className="mt-1 text-sm text-session-muted">{formatDateTime(interview.scheduledAt)}</p>
        </div>
        <StateBadge state={interview.state} />
      </div>

      <section className="panel mt-8 p-5" aria-labelledby="participants">
        <h2 id="participants" className="text-sm font-semibold text-session-text">
          Participants
        </h2>
        <dl className="mt-4 grid gap-x-8 gap-y-4 sm:grid-cols-2">
          <div>
            <dt className="field-label">Candidate</dt>
            <dd className="text-sm text-session-text">
              {interview.candidate.fullName}
              <span className="ml-2 text-session-dim">{interview.candidate.email}</span>
              <span className="ml-2 text-xs text-session-dim capitalize">
                ({interview.candidate.status})
              </span>
            </dd>
          </div>
          <div>
            <dt className="field-label">Interviewers</dt>
            <dd className="space-y-1 text-sm text-session-text">
              {interview.interviewers.map((person) => (
                <p key={person.id}>
                  {person.fullName}
                  <span className="ml-2 text-session-dim">{person.email}</span>
                </p>
              ))}
            </dd>
          </div>
          <div>
            <dt className="field-label">Room</dt>
            <dd className="text-sm text-session-text">{interview.roomName}</dd>
          </div>
          <div>
            <dt className="field-label">Started / ended</dt>
            <dd className="text-sm text-session-text">
              {interview.startedAt ? formatDateTime(interview.startedAt) : '—'}
              {' → '}
              {interview.endedAt ? formatDateTime(interview.endedAt) : '—'}
            </dd>
          </div>
        </dl>
      </section>

      <ScheduleControls interview={interview} />

      <section className="mt-6" aria-labelledby="invitations">
        <h2 id="invitations" className="mb-3 text-sm font-semibold text-session-text">
          Invitations
        </h2>
        {invitations.isPending ? (
          <p className="panel px-5 py-6 text-sm text-session-muted">Loading invitations…</p>
        ) : invitations.error ? (
          <p className="panel px-5 py-6 text-sm text-session-muted">
            {messageForError(invitations.error)}
          </p>
        ) : invitations.data.length === 0 ? (
          <p className="panel px-5 py-6 text-sm text-session-muted">
            No invitations were issued for this interview.
          </p>
        ) : (
          <ul className="panel overflow-hidden">
            {invitations.data.map((invitation) => (
              <InvitationRow
                key={invitation.id}
                invitation={invitation}
                people={people}
                interviewId={interviewId}
              />
            ))}
          </ul>
        )}
      </section>

      <section className="mt-6" aria-labelledby="telemetry">
        <div className="mb-3 flex items-center justify-between gap-4">
          <h2 id="telemetry" className="text-sm font-semibold text-session-text">
            Telemetry log
          </h2>
          <div className="flex gap-2">
            {(['json', 'csv'] as const).map((format) => (
              <button
                key={format}
                type="button"
                onClick={() => void onExport(format)}
                disabled={exporting !== null}
                className="btn-secondary px-3 py-1.5 text-xs"
              >
                {exporting === format ? 'Exporting…' : `Export ${format.toUpperCase()}`}
              </button>
            ))}
          </div>
        </div>

        {exportError ? (
          <p role="alert" className="form-error mb-3">
            {exportError}
          </p>
        ) : null}

        {telemetry.isPending ? (
          <p className="panel px-5 py-6 text-sm text-session-muted">Loading telemetry…</p>
        ) : telemetry.error ? (
          <p className="panel px-5 py-6 text-sm text-session-muted">
            {messageForError(telemetry.error)}
          </p>
        ) : (
          <TelemetryLogView events={telemetry.data.events} />
        )}
      </section>
    </div>
  );
}
